import { ApplicationConfig, provideZoneChangeDetection, isDevMode, APP_INITIALIZER } from '@angular/core';
import { provideHttpClient, withInterceptors, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { TitleStrategy, provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { NgcCookieConsentConfig, NgcCookieConsentModule, provideNgcCookieConsent } from 'ngx-cookieconsent';
import { provideServiceWorker } from '@angular/service-worker';
import { TemplateTitleStrategy } from './core/template-title.strategy';
import { AuthInterceptor } from './Interceptors/auth.interceptor';
import { ErrorInterceptor } from './Interceptors/error.interceptor';
import { EncryptionService } from './Services/encryption';

// Heuristic for our encrypted format: base64_iv:base64_ciphertext
const ENCRYPTED_RE = /^[A-Za-z0-9+/=]+:[A-Za-z0-9+/=]+$/;

export function initStorageEncryptionFactory(encryptionService: EncryptionService) {
  return () => {
    try {
      if (typeof window === 'undefined' || !window?.localStorage) return;
      const proto = Storage.prototype as any;
      const originalSet = proto.setItem;

      proto.setItem = function (key: string, value: string) {
        try {
          const str = (typeof value === 'string') ? value : JSON.stringify(value);
          // If already encrypted, write as-is
          if (ENCRYPTED_RE.test(str.trim())) {
            return originalSet.call(this, key, str);
          }
          const encrypted = encryptionService.encrypt(str);
          return originalSet.call(this, key, encrypted);
        } catch (err) {
          try { return originalSet.call(this, key, value as any); } catch (e) { /* ignore */ }
        }
      };
    } catch (e) {
      console.warn('Storage encryption initializer failed', e);
    }
  };
}

export const STORAGE_ENCRYPTION_PROVIDER = {
  provide: APP_INITIALIZER,
  useFactory: initStorageEncryptionFactory,
  deps: [EncryptionService],
  multi: true
};

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: 'localhost' // or 'your.domain.com' // it is mandatory to set a domain, for cookies to work properly (see https://goo.gl/S2Hy2A)
  },
  palette: {
    popup: {
      background: '#000'
    },
    button: {
      background: '#f1d600'
    }
  },
  theme: 'edgeless',
  type: 'opt-out'
};


export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: TemplateTitleStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    provideClientHydration(withEventReplay()),
    provideNgcCookieConsent(cookieConfig),
    STORAGE_ENCRYPTION_PROVIDER,
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    })
  ]
};

